import HelpIcon from 'assets/icons/help-black.svg';
import LighterGreyHelpIcon from 'assets/icons/help-lighter-grey.svg';
import { decode } from 'base-64';
import DataEntryModal from 'components/DataEntryModal';
import Footer from 'components/Footer';
import FullWidthSection from 'components/FullWidthSection';
import GeoChart from 'components/GeoChart';
import GrowthEnterprise from 'components/GrowthEnterprise';
import Header from 'components/Header';
import LongerDisclousure from 'components/LongerDisclousure';
import SectionCharts from 'components/SectionCharts';
import LoadingComponent from 'components/SectionCharts/LoadingComponent';
import SubscribeSection from 'components/SubscribeSection';
import CustomTooltip from 'components/Tooltip';
import WarningModal from 'components/WarningModal';
import MaintenanceModal from 'components/MaintenanceModal';
import { getData } from 'network/getData';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { isMobile, isTablet } from 'react-device-detect';
import { useMediaQuery } from 'react-responsive';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import {
  setCompanyDetails,
  setRequiredInfo,
  setGrowthAndRetention,
  setOperationalEfficiency,
  setHeadCount,
  setCompanyEmail,
  setIsEntered
} from 'store/companyInfo/companyInfo.slice';
import { setIsLoading } from 'store/loadingChart/loadingChart.slice';
import {
  mockedBarChartOptions,
  pricingModelData,
  subSectorData
} from 'utils/chart-options/mocked-chart-data';
import { companyStatus, modalModes, urlParamNames } from 'utils/constants';
import { dashboardWalkthroughSteps } from 'utils/constants/tutorials-walkthrough';
import {
  ChartDataMap,
  getAvgDealSizeLabel,
  generateDisabledFiltersMap
} from 'utils/helpers/chartsDataHelpers';
import './App.css';
import Joyride, { ACTIONS, CallBackProps, STATUS } from 'react-joyride';
import CustomWalkthroughTooltip from 'components/CustomWalkthroughTooltip';
import { useDarkMode } from 'utils/hooks/DarkMode';
import AboutModal from 'components/AboutModal';
import EnterDataSection from 'components/enterDataSection';
import { useDependencyUpdates } from 'utils/hooks/dependencyUpdates';
import { setDisabledFilters } from 'store/disabledFilters/disabledFilters.slice';
import RequiredInfoModal from 'components/RequiredInfoModal';
import PendingBanner from 'components/PendingBanner';
import ComingSoonBanner from 'components/ComingSoonBanner';
import { useAuth } from 'context/AuthContext';
import ComplianceModal from 'components/ComplianceModal';
import Chatbot from 'components/Chatbot';
import { useNavigate } from 'react-router';
import { getUserHistoricalCompsData } from 'network/getUserHistoricalCompsData';
import { transformToQuarterData } from 'components/EditableTable/utils/mapPortCoData';
import {
  setChartDataIdx,
  setChartsDataSourceIdx,
  setDefaultQuarterIdx,
  setisDataReviewed,
  setPortCoChartsData,
  setPortCoData,
  setPortCoLabels
} from 'store/portCoData/portCoData.slice';
import { RowWithDate } from 'components/EditableTable/types';
import { filterPortCoData } from 'components/EditableTable/utils/filterPortCoData';

function App() {
  const { isApproved, isPortCo, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const updateDependentFields = useDependencyUpdates();

  const isDarkMode = useDarkMode('(prefers-color-scheme: dark)');

  const [data, setData] = useState<ChartDataMap[]>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const isDesktop = useMediaQuery({ query: '(min-width: 1024px)' });
  const [warningModalOpen, setWarningModalOpen] = useState(!isDesktop);
  const [warningModalConfirmed, setWarningModalConfirmed] = useState(false);
  const [walkthroughActive, setWalkthroughActive] = useState(false);
  const [isComplianceModalOpen, setComplianceModalOpen] = useState(false);

  const xAxisSwitch = useAppSelector(
    (state: RootState) => state.chartSwitches.xAxisSwitch
  );

  const timeDurationSwitch = useAppSelector(
    (state: RootState) => state.chartSwitches.timeDurationSwitch
  );

  const [showMaintenance, setShowMaintenance] = useState(false);
  const [until, setUntil] = useState('');

  const isLoading = useAppSelector(
    (state: RootState) => state.loadingChart.isLoading
  );

  const loadPortCoUserData = useCallback(
    (data: RowWithDate[]) => {
      const { filteredData, defaultQuarterIdx } = filterPortCoData(data);

      dispatch(setPortCoChartsData(filteredData));
      dispatch(setChartDataIdx(defaultQuarterIdx));
      dispatch(setChartsDataSourceIdx(defaultQuarterIdx === 4 ? 0 : 1));
    },
    [dispatch]
  );

  const loadData = useCallback(async () => {
    dispatch(setIsLoading(true));
    setErrorMessage(null);
    try {
      const data = await getData();
      // trim any extra sapces in ARR_RANGE attribute
      const cleanedData = data.map(item => ({
        ...item,
        ARR_RANGE: item.ARR_RANGE.trim(),
        AVG_DEAL_SIZE: getAvgDealSizeLabel(item.AVG_CONTRACT_VALUE)
      }));
      setData(cleanedData);
    } catch (error: any) {
      setErrorMessage(error.message);
      dispatch(setIsLoading(false));
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [dispatch]);

  const saveDataToRedux = useCallback(
    (urlParams: URLSearchParams) => {
      const storedLogo = localStorage.getItem('companyLogoURL');

      dispatch(
        setRequiredInfo({
          status: urlParams.get(urlParamNames.COMPANY_STATUS)
            ? urlParams.get(urlParamNames.COMPANY_STATUS) ===
              companyStatus.PRIVATE
              ? companyStatus.PRIVATE
              : companyStatus.PUBLIC
            : null,
          currentARR: urlParams.get(urlParamNames.CURRENT_ARR)
            ? Number(urlParams.get(urlParamNames.CURRENT_ARR))
            : null,
          revenue: urlParams.get(urlParamNames.REVENUE)
            ? Number(urlParams.get(urlParamNames.REVENUE))
            : null,
          fcfMargin: urlParams.get(urlParamNames.FCF_MARGIN)
            ? Number(urlParams.get(urlParamNames.FCF_MARGIN))
            : null,
          avgDealSize: urlParams.get(urlParamNames.AVG_DEAL_SIZE)
            ? Number(urlParams.get(urlParamNames.AVG_DEAL_SIZE))
            : null,
          totalHeadcount: urlParams.get(urlParamNames.TOTAL_HEADCOUNT)
            ? Number(urlParams.get(urlParamNames.TOTAL_HEADCOUNT))
            : null,
          grossMargin: urlParams.get(urlParamNames.GROSS_MARGIN)
            ? Number(urlParams.get(urlParamNames.GROSS_MARGIN))
            : null,
          yoyARR: urlParams.get(urlParamNames.YOY_ARR)
            ? Number(urlParams.get(urlParamNames.YOY_ARR))
            : null,
          arrPerFTE: urlParams.get(urlParamNames.ARR_PER_FTE)
            ? Number(urlParams.get(urlParamNames.ARR_PER_FTE))
            : null,
          ruleOf40: urlParams.get(urlParamNames.RULE_OF_40)
            ? Number(urlParams.get(urlParamNames.RULE_OF_40))
            : null
        })
      );
      dispatch(
        setGrowthAndRetention({
          newLogoArrOfGrossNew: urlParams.get(
            urlParamNames.NEW_LOGO_ARR_OF_GROSS_NEW
          )
            ? Number(urlParams.get(urlParamNames.NEW_LOGO_ARR_OF_GROSS_NEW))
            : null,
          expansionArrOfGrossNew: urlParams.get(
            urlParamNames.EXPANSION_ARR_OF_GROSS_NEW
          )
            ? Number(urlParams.get(urlParamNames.EXPANSION_ARR_OF_GROSS_NEW))
            : null,
          quickRatio: urlParams.get(urlParamNames.QUICK_RATIO)
            ? Number(urlParams.get(urlParamNames.QUICK_RATIO))
            : null,
          netDollarRetention: urlParams.get(urlParamNames.NET_DOLLAR_RETENTION)
            ? Number(urlParams.get(urlParamNames.NET_DOLLAR_RETENTION))
            : null,
          grossDollarRetention: urlParams.get(
            urlParamNames.GROSS_DOLLAR_RETENTION
          )
            ? Number(urlParams.get(urlParamNames.GROSS_DOLLAR_RETENTION))
            : null,
          newLogoVelocity: urlParams.get(urlParamNames.NEW_LOGO_VELOCITY)
            ? Number(urlParams.get(urlParamNames.NEW_LOGO_VELOCITY))
            : null
        })
      );
      dispatch(
        setOperationalEfficiency({
          burnMultiple: urlParams.get(urlParamNames.BURN_MULTIPLE)
            ? Number(urlParams.get(urlParamNames.BURN_MULTIPLE))
            : null,
          netMagicNumber: urlParams.get(urlParamNames.NET_MAGIC_NUMBER)
            ? Number(urlParams.get(urlParamNames.NET_MAGIC_NUMBER))
            : null,
          rD: urlParams.get(urlParamNames.RD)
            ? Number(urlParams.get(urlParamNames.RD))
            : null,
          gA: urlParams.get(urlParamNames.GA)
            ? Number(urlParams.get(urlParamNames.GA))
            : null,
          sM: urlParams.get(urlParamNames.SM)
            ? Number(urlParams.get(urlParamNames.SM))
            : null,
          cacPaybackPeriod: urlParams.get(urlParamNames.CAC_PAYBACK_PERIOD)
            ? Number(urlParams.get(urlParamNames.CAC_PAYBACK_PERIOD))
            : null,
          rdPercent: urlParams.get(urlParamNames.RD_PERCENT)
            ? Number(urlParams.get(urlParamNames.RD_PERCENT))
            : null,
          gaPercent: urlParams.get(urlParamNames.GA_PERCENT)
            ? Number(urlParams.get(urlParamNames.GA_PERCENT))
            : null,
          smPercent: urlParams.get(urlParamNames.SM_PERCENT)
            ? Number(urlParams.get(urlParamNames.SM_PERCENT))
            : null,
          totalOpex: urlParams.get(urlParamNames.TOTAL_OPEX)
            ? Number(urlParams.get(urlParamNames.TOTAL_OPEX))
            : null
        })
      );
      dispatch(
        setHeadCount({
          opexPerFTE: urlParams.get(urlParamNames.OPEX_PER_FTE)
            ? Number(urlParams.get(urlParamNames.OPEX_PER_FTE))
            : null,
          rDHead: urlParams.get(urlParamNames.RD_HEAD)
            ? Number(urlParams.get(urlParamNames.RD_HEAD))
            : null,
          gAHead: urlParams.get(urlParamNames.GA_HEAD)
            ? Number(urlParams.get(urlParamNames.GA_HEAD))
            : null,
          sMHead: urlParams.get(urlParamNames.SM_HEAD)
            ? Number(urlParams.get(urlParamNames.SM_HEAD))
            : null,
          rDHeadPercent: urlParams.get(urlParamNames.RD_HEAD_PERCENT)
            ? Number(urlParams.get(urlParamNames.RD_HEAD_PERCENT))
            : null,
          gAHeadPercent: urlParams.get(urlParamNames.GA_HEAD_PERCENT)
            ? Number(urlParams.get(urlParamNames.GA_HEAD_PERCENT))
            : null,
          sMHeadPercent: urlParams.get(urlParamNames.SM_HEAD_PERCENT)
            ? Number(urlParams.get(urlParamNames.SM_HEAD_PERCENT))
            : null
        })
      );
      dispatch(
        setCompanyDetails({
          companyLogoURL: storedLogo,
          companyColor: urlParams.get(urlParamNames.BRAND_COLOR) || '',
          companyWebsite: urlParams.get(urlParamNames.COMPANY_WEBSITE) || ''
        })
      );
      dispatch(setCompanyEmail(urlParams.get(urlParamNames.EMAIL) || ''));
      dispatch(setIsEntered(true));
    },
    [dispatch]
  );

  const checkURL = useCallback(() => {
    const params = new URL(document.URL).searchParams;
    const encodedParams = params.get('params');
    if (encodedParams) {
      let decodedParams = '';
      try {
        decodedParams = decode(encodedParams);
      } catch (_error) {
        console.log('Failed to decode');
      }
      const urlParams = new URLSearchParams(decodedParams);
      if (urlParams.get(urlParamNames.COMPANY_STATUS)) {
        saveDataToRedux(urlParams);
        sessionStorage.setItem('uuid', urlParams.get('uuid') || '');
      }
    }
  }, [saveDataToRedux]);

  useEffect(() => {
    const loadPortCoData = async () => {
      const initialData = await getUserHistoricalCompsData();
      const defaultQuarter = initialData.data?.[0].COMPASS_DEFAULT_QUARTER;

      const { rows, labels } = transformToQuarterData(initialData.data);

      const defaultQuarterIdx = labels.findIndex(
        label =>
          label ===
          ((defaultQuarter as string) || '')
            ?.replace('Q', 'q')
            ?.replace(' ', '_')
      );

      dispatch(setPortCoData(rows));
      dispatch(setPortCoLabels(labels));
      dispatch(
        setDefaultQuarterIdx(
          defaultQuarterIdx === -1
            ? initialData?.data?.length - 1
            : defaultQuarterIdx
        )
      );
      dispatch(setisDataReviewed(!!initialData.data_is_reviewed));

      loadPortCoUserData(initialData.data);

      setComplianceModalOpen(!initialData.data_is_reviewed);
    };

    if (isAuthenticated && isPortCo === true) loadPortCoData();
  }, [isAuthenticated, isPortCo, dispatch, loadPortCoUserData]);

  useEffect(() => {
    if (!isDesktop && !(isMobile || isTablet)) {
      setWarningModalOpen(true);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (
      localStorage.getItem('skip_dashboard_tutorial') !== 'true' &&
      !warningModalOpen &&
      !isLoading
    ) {
      setWalkthroughActive(true);
    } else {
      setWalkthroughActive(false);
    }
  }, [warningModalOpen, isLoading]);

  useEffect(() => {
    if (!isPortCo) {
      checkURL();
    }

    loadData();
    if (!localStorage.getItem('uuid')) {
      const uuid = crypto.randomUUID();
      localStorage.setItem('uuid', uuid);
    }
    const maintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';
    const maintenanceUntil = process.env.REACT_APP_MAINTENANCE_UNTIL;

    if (maintenanceMode && maintenanceUntil) {
      setShowMaintenance(true);
      setUntil(maintenanceUntil);
    }
  }, [checkURL, isPortCo, loadData]);

  const handleWalkthroughCallback = (data: CallBackProps) => {
    if (data.action === ACTIONS.SKIP) {
      localStorage.setItem('skip_dashboard_tutorial', 'true');
    }
    if (data.status === STATUS.FINISHED) {
      localStorage.setItem('skip_dashboard_tutorial', 'true');
      window.scrollTo(0, 0);
    }
  };

  const onCloseWarningModal = () => {
    document
      .querySelector('meta[name=viewport]')
      ?.setAttribute('content', 'width=1280, initial-scale=0');
    setWarningModalOpen(false);
    setWarningModalConfirmed(true);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState<boolean>(false);

  const companyInfo = useAppSelector(state => state.companyInfo);

  const [modalMode, setModalMode] = useState(modalModes.ADD);

  const onClose = () => {
    setIsOpen(false);
  };

  const toggleAboutModal = () => {
    setIsAboutModalOpen(!isAboutModalOpen);
  };

  const onGrowthEnterpriseUpdate = () => {
    setModalMode(modalModes.EDIT);
    setIsOpen(true);
  };

  const onGrowthEnterpriseReset = () => {
    setModalMode(modalModes.ADD);
    window.history.pushState({}, '', '/');
  };

  const handleMyData = () => {
    if (isPortCo === true) {
      navigate('/company-data');
    } else {
      setIsOpen(true);
    }
  };

  const disabledFiltersMap = useMemo(() => {
    if (data && data.length) {
      const filteredData = timeDurationSwitch
        ? data
        : data.filter(d => d.LAST_4_YEAR_FILTER);

      return generateDisabledFiltersMap(xAxisSwitch, filteredData);
    }
    return {};
  }, [xAxisSwitch, timeDurationSwitch, data]);

  useEffect(() => {
    dispatch(setDisabledFilters(disabledFiltersMap));
  }, [disabledFiltersMap, dispatch]);

  const joyrideOverlayWidth =
    !(isMobile || isTablet) && warningModalConfirmed ? '1280px' : '100%';

  return (
    <div className='App dark:text-white dark:bg-black'>
      <main
        className={`App-header max-w-[1660px] mx-auto ${
          !(isMobile || isTablet) && warningModalConfirmed ? 'w-[1280px]' : ''
        }`}
      >
        <Header
          handleMyData={handleMyData}
          toggleAboutModal={toggleAboutModal}
        />
        {isPortCo && !isApproved && <PendingBanner />}
        <ComingSoonBanner />
        <div className='w-full dark:bg-black'>
          <EnterDataSection
            onComparePerformance={handleMyData}
            onUpdate={onGrowthEnterpriseUpdate}
            isDarkMode={isDarkMode}
          />
          {companyInfo.isEntered && !isPortCo && (
            <GrowthEnterprise onUpdate={onGrowthEnterpriseUpdate} />
          )}
          {!data || isLoading ? (
            <LoadingComponent errorMessage={errorMessage} />
          ) : (
            <SectionCharts chartDataMap={data} />
          )}
        </div>
        <div className='w-full flex flex-col items-start px-20 pt-10 pb-20 gap-10'>
          <FullWidthSection
            title='Firmographics'
            id='firmographics_section'
            description='Custom benchmarking powered by quarterly operating and financial data from global software companies.'
            isAccordion
          >
            <div className='flex flex-row w-full gap-4'>
              <div className='w-[calc(50%-8px)] border border-solid border-gray-400 rounded-[4px] max-h-[350px]'>
                <div className='flex flex-row items-center w-full h-6 gap-1 pl-6 pt-6'>
                  <p className='text-xl'>Pricing Model</p>
                  <CustomTooltip
                    id='pricingModel'
                    content='The distribution of companies in our dataset based on their primary pricing model'
                    icon={isDarkMode ? LighterGreyHelpIcon : HelpIcon}
                  />
                </div>
                <div className='h-full w-full'>
                  <Bar
                    data={pricingModelData}
                    options={mockedBarChartOptions()}
                  />
                </div>
              </div>

              <div className='w-[calc(50%-8px)] border border-solid border-gray-400 rounded-[4px] max-h-[350px]'>
                <div className='flex flex-row items-center w-full h-6 gap-1 pl-6 pt-6'>
                  <p className='text-xl'>Sub-Sector</p>
                  <CustomTooltip
                    id='subSector'
                    content='The distribution of companies in our dataset based on their sub-sector'
                    icon={isDarkMode ? LighterGreyHelpIcon : HelpIcon}
                  />
                </div>
                <div className='h-full w-full'>
                  <Bar data={subSectorData} options={mockedBarChartOptions()} />
                </div>
              </div>
            </div>

            <div className='w-full mt-4'>
              <div className='w-full min-w-[200px] border border-solid border-gray-400 rounded-[4px] h-[466px]'>
                <div className='flex flex-row items-center w-full h-6 gap-1 pl-6 pt-6'>
                  <p className='text-xl'>Geography</p>
                  <CustomTooltip
                    id='geography'
                    content='The distribution of companies in our dataset across geographies based on primary headquarters'
                    icon={isDarkMode ? LighterGreyHelpIcon : HelpIcon}
                  />
                </div>
                <div className='h-full w-full'>
                  <GeoChart />
                </div>
              </div>
            </div>
          </FullWidthSection>
          <SubscribeSection />
          <div className='w-full py-6 bg-white dark:bg-black flex flex-col items-center border border-gray-400 dark:border-gray-850 rounded-lg'>
            <p className='text-xl font-semibold'>We love to hear from you!</p>
            <p className='text-lg font-light text-gray-700 dark:text-white'>
              Please send us any feedback by{' '}
              <a
                id='email_link'
                href='mailto:ICONIQGrowthInsights@iconiq.com'
                className='text-blue-400 border-blue-400 text-lg font-light'
              >
                dropping a note here
              </a>
            </p>
          </div>
          <LongerDisclousure />
        </div>
        <Footer />
        <WarningModal isOpen={warningModalOpen} onClose={onCloseWarningModal} />
        <DataEntryModal
          isOpen={isOpen}
          onClose={onClose}
          mode={modalMode}
          onGrowthEnterpriseReset={onGrowthEnterpriseReset}
          updateDependentFields={updateDependentFields}
        />
        <AboutModal isOpen={isAboutModalOpen} onClose={toggleAboutModal} />
        <ComplianceModal isOpen={isComplianceModalOpen} />
        <RequiredInfoModal />
        <ToastContainer theme={isDarkMode ? 'dark' : 'light'} />
        {!showMaintenance && (
          <Joyride
            continuous
            disableOverlayClose
            scrollDuration={1000}
            run={walkthroughActive}
            locale={{ last: 'Finish' }}
            steps={dashboardWalkthroughSteps}
            callback={handleWalkthroughCallback}
            styles={{
              options: { arrowColor: 'white' },
              overlay: { width: joyrideOverlayWidth }
            }}
            tooltipComponent={CustomWalkthroughTooltip}
          />
        )}
      </main>

      {/* growth disclaimer */}
      <section className='section_companies-disclaimer'>
        <div className='padding-global'>
          <div className='container-large'>
            <div className='padding-section-xsmall is-xxs-mobile'>
              <div className='growth-disclaimer'>
                The ICONIQ Venture and Growth website does not present
                information relating to ICONIQ, its investment funds, or its
                advisory business and should not be consulted for any advisory
                purposes. The ICONIQ Venture and Growth content is intended for
                the use of company founders and executives.
              </div>
            </div>
          </div>
        </div>
      </section>
      {showMaintenance && <MaintenanceModal until={until} />}
      <Chatbot />
    </div>
  );
}

export default App;
